import { AUTH_CONTEXT } from "./AuthContext";
import { useAuth0 } from "@auth0/auth0-react";
import { Text } from "@chakra-ui/react";
import { setGlobal } from "../global";
import { useEffect } from "react";
import { esgAdminKeys, useGetOrganizationModules } from "../../hooks/query/esg-admin/esg-admin";
import { useQuery } from "react-query";
import { OrganizationAdministration } from "../../api";

/**
 * Auth + user-permissions provider for the application.
 *
 * This will set the access token in the global context, which is then used by all axios requests.
 * This simplifies the code, locating access token logic in one place, rather than managing access tokens for each API request.
 */
export default function AuthProvider({ children }) {
  const { user, logout: logoutAuth0, getAccessTokenSilently: getAccessTokenSilentlyAuth0 } = useAuth0();

  // Organization Roles are stored in the Auth0 user object
  // Note: calling these permissions looks like a mistake -- these are roles, which then can give a list of permissions
  const userPermissions = user[process.env.REACT_APP_USER_PERMISSIONS_NAMESPACE] || [];

  const getAccessTokenSilently = () => {
    return getAccessTokenSilentlyAuth0().then((token) => {
      console.log("AuthProvider - setting GLOBAL.accessToken");
      setGlobal("accessToken", token);
      return token;
    });
  };

  const logout = () => {
    setGlobal("accessToken", null);
    setGlobal("organizationModules", null);
    logoutAuth0({ returnTo: window.location.origin });
  };

  const callApi = (method, params) => {
    return getAccessTokenSilently().then((token) => {
      return params !== undefined ? method(params, token) : method(token);
    });
  };

  useQuery(...esgAdminKeys.orgModules(), () => callApi(OrganizationAdministration.useGetOrganizationModules), {
    onSuccess: (data) => {
      console.log("AuthProvider - setting GLOBAL.organizationModules", data);
      setGlobal("organizationModules", data);
    },
    enabled: !!user,
  });

  /** @type {Auth} */
  const authContextValue = { user, userPermissions, logout, callApi, getAccessTokenSilently };

  return userPermissions.length === 0 ? (
    <Text>You are not authorized to access this system!</Text>
  ) : (
    <AUTH_CONTEXT.Provider value={authContextValue}>{children}</AUTH_CONTEXT.Provider>
  );
}
